<script setup lang="ts">
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import SearchableSelectList, { type ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import { getFullName } from '@/utils/formatters'
import type { IPositionEmployeeItem } from '../../types/settings/IStaffPositionEmployeesResponse'

const props = defineProps<{
  modelValue: boolean
  employees: IPositionEmployeeItem[]
  positions: ISearchableSelectItem[]
  loading?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [positionId: string]
  remove: [id: string]
  'remove-all': []
}>()

const targetId = ref<string | null>(null)
const menuOpen = ref(false)

const selectedItems = computed(() => props.employees.map(item => ({
  id: String(item.id),
  name: getFullName(item) || 'Сотрудник',
  avatar: item.avatar || undefined,
  description: item.department_name || undefined,
})))

const selectedPositionName = computed(() => {
  if (!targetId.value) return ''
  const item = props.positions.find(position => String(position.id) === targetId.value)
  return item?.label || ''
})

watch(() => props.modelValue, open => {
  if (!open) return
  targetId.value = null
  menuOpen.value = false
})

function close() {
  emit('update:modelValue', false)
}

function onSelect(item: ISearchableSelectItem) {
  targetId.value = String(item.id)
  menuOpen.value = false
}

function submit() {
  if (!targetId.value || !props.employees.length || props.loading) return
  emit('confirm', targetId.value)
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="560"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="pa-6 d-flex flex-column ga-4 bg-surface">
      <div class="d-flex align-center justify-space-between ga-3">
        <span class="text-h4 staff-modal-title">Перевод сотрудников</span>
        <CustomCloseButton :close-action="close" />
      </div>

      <VMenu
        open-on-hover
        :close-on-content-click="false"
        close-delay="200"
        location="bottom start"
        offset="8"
      >
        <template #activator="{ props: menuProps }">
          <span
            v-bind="menuProps"
            class="text-body-1 text-medium-emphasis cursor-pointer"
          >
            Выбрано сотрудников: {{ employees.length }}
          </span>
        </template>
        <CustomSelectedItems
          :items="selectedItems"
          :title="`Выбрано ${employees.length}:`"
          @remove="emit('remove', String($event))"
          @remove-all="emit('remove-all')"
        />
      </VMenu>

      <VMenu
        v-model="menuOpen"
        :close-on-content-click="false"
        :max-height="350"
        location="bottom"
        transition="scale-transition"
        content-class="entity-form-menu tree-select-menu"
      >
        <template #activator="{ props: menuProps }">
          <VTextField
            :model-value="selectedPositionName"
            class="invisible-readonly cursor-pointer"
            :append-inner-icon="menuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
            label="Куда перевести"
            placeholder="Выберите должность"
            variant="outlined"
            density="comfortable"
            persistent-placeholder
            readonly
            clearable
            hide-details
            v-bind="menuProps"
            @click:append-inner.stop="menuOpen = !menuOpen"
            @click:clear="targetId = null"
          />
        </template>
        <SearchableSelectList
          :items="positions"
          placeholder="Поиск должности..."
          @select="onSelect"
        />
      </VMenu>

      <div class="d-flex justify-end">
        <VBtn
          color="primary"
          :disabled="!targetId || !employees.length"
          :loading="loading"
          @click="submit"
        >
          Перевести
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.staff-modal-title {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
}
</style>
